<template>
  <section class="w-full" aria-labelledby="ev-procedure-legend">
    <!-- Mismo contenedor y ancho que question-sfc-procedure (960px) -->
    <div class="single-window-container mx-auto px-5 sm:px-6 lg:px-8">
      <!-- Card contenedor: mismo padding que question-sfc-procedure -->
      <div
        class="bg-white rounded-lg shadow-sm border border-gray-200 px-5 py-8 sm:px-8 sm:py-10 md:px-16 md:py-12 text-left">
        <!-- Enlace Anterior (alineado a la izquierda) -->
        <div class="text-left mb-4">
          <button type="button"
            class="inline-flex items-center gap-2 text-base font-semibold text-blue focus:outline-none" @click="onBack">
            <img :src="returnIcon" class="w-5 h-4" alt="" aria-hidden="true" />
            Anterior
          </button>
        </div>
        <fieldset class="m-0 p-0 border-0 min-w-0">
          <legend id="ev-procedure-legend" ref="legend" tabindex="-1"
            class="w-full mb-4 p-0 border-0 text-base font-semibold text-gray-800 focus:outline-none">
            Seleccione la opción que quiere realizar:
          </legend>
          <div class="flex flex-col gap-4">
            <div v-for="option in options" :key="option.id" :class="[
              'relative w-full border rounded-md bg-white transition-colors duration-200',
              isSelected(option)
                ? 'border-blue shadow-lg'
                : 'border-blue-light hover:border-blue',
            ]">
              <label :for="inputId(option)"
                class="flex items-center gap-4 md:gap-6 w-full pt-4 pl-4 pb-2 pr-12 md:pr-14 rounded-md cursor-pointer focus-within:ring-2 focus-within:ring-blue">
                <input :id="inputId(option)" v-model="selected" type="radio" name="ev-procedure-option"
                  :value="option.id" :aria-labelledby="titleId(option)" :aria-describedby="descId(option)"
                  class="sr-only" />
                <span aria-hidden="true" :class="[
                  'absolute top-4 right-4 md:top-6 md:right-6 flex items-center justify-center w-6 h-6 rounded-full border-2 transition-colors duration-200',
                  isSelected(option) ? 'bg-blue border-blue text-white' : 'bg-white border-gray-300',
                ]">
                  <svg v-if="isSelected(option)" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                    stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" focusable="false" class="w-4 h-4">
                    <path d="M3.5 8.5l3 3 6-7" />
                  </svg>
                </span>
                <span class="flex items-center justify-center flex-shrink-0 w-20 h-20 rounded-full bg-white shadow-lg">
                  <img :src="option.icon" alt="" aria-hidden="true" class="ventanilla-icon w-12 h-12" />
                </span>
                <span class="flex flex-col gap-1 min-w-0">
                  <span :id="titleId(option)" class="block text-base font-bold text-blue-dark leading-snug">
                    {{ option.title }}
                  </span>
                  <span :id="descId(option)" class="block text-sm text-gray-600">
                    {{ option.description }}
                  </span>
                </span>
              </label>
              <div v-if="isSelected(option)" class="p-4 pt-0 md:p-6 md:pt-0">
                <!-- La línea, el texto y el botón se ubican en la misma columna que
                   el texto del título: a la izquierda se deja libre el ancho del
                   ícono (w-16) más el gap (gap-4 / md:gap-6), igual que el label. -->
                <div class="ventanilla-expanded-content">
                  <hr class="m-0 mb-4 h-0 border-0 border-t border-blue-light" />
                  <p class="m-0 text-sm text-gray-700">{{ option.details }}</p>
                  <ol v-if="option.infoItems && option.infoItems.length"
                    class="list-decimal pl-5 mt-3 mb-0 text-sm text-gray-700">
                    <li v-for="(item, index) in option.infoItems" :key="index">
                      <!-- Pendiente: el enlace de "clic aquí" aún no tiene URL definida; al tenerla, agregar href, target="_blank" y rel="noopener noreferrer". -->
                      {{ item.text }} <a class="text-blue underline hover:text-blue hover:underline">{{ item.linkText
                      }}</a>
                    </li>
                  </ol>
                </div>
                <!-- El botón se centra respecto a toda la tarjeta, por eso queda
                   fuera del contenedor indentado (.ventanilla-expanded-content). -->
                <div class="flex justify-center mt-6">
                  <a class="inline-flex items-center justify-center px-8 py-2 rounded-full bg-blue text-white text-base font-semibold transition hover:bg-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue no-underline hover:text-white"
                    @click="onStart(option)">
                    <span>Empezar</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  </section>
</template>

<script>
import reportComplaintIcon from "@/assets/icons/single_window/report-complaint.svg";
import conciliationIcon from "@/assets/icons/single_window/conciliation.svg";
import justiceServicesIcon from "@/assets/icons/single_window/justice-services.svg";
import returnIcon from "@/assets/icons/return.svg";

// Marcador para destinos aún no definidos. El control "Empezar" se muestra
// activo pero no navega mientras el enlace esté pendiente.
const PENDING_LINK = "#";
const isPendingLink = (href) => !href || href === PENDING_LINK;

// Destinos del control "Empezar" por id de opción. Aún pendientes de definir.
const EV_PROCEDURE_LINKS = {
  inconformidad: PENDING_LINK,
  conciliacion: PENDING_LINK,
  servicios_justicia: PENDING_LINK,
};

const startHrefFor = (id) => {
  const href = Object.prototype.hasOwnProperty.call(EV_PROCEDURE_LINKS, id)
    ? EV_PROCEDURE_LINKS[id]
    : undefined;
  return isPendingLink(href) ? PENDING_LINK : href;
};

export default {
  name: "QuestionEvProcedure",
  data() {
    return {
      returnIcon,
      // La opción seleccionada se expande; los destinos de "Empezar" (EV_PROCEDURE_LINKS)
      // y el enlace "clic aquí" siguen pendientes de definir.
      selected: null,
      options: [
        {
          id: "inconformidad",
          icon: reportComplaintIcon,
          title: "Reportar una inconformidad con una entidad del sistema financiero",
          description: "Su solicitud será atendida directamente por la entidad financiera.",
          details: "El término máximo de duración para una inconformidad es de 15 días, prorrogables de manera sustentada por la entidad vigilada.",
        },
        {
          id: "conciliacion",
          icon: conciliationIcon,
          title: "Conciliar con una entidad del sistema financiero",
          description: "Será convocado para que asista a una audiencia junto con la entidad para buscar una solución a su inconformidad, con la participación de un Conciliador de la Superfinanciera.",
          details: "El tiempo de duración de una Conciliación Extrajudicial es de 3 meses, las partes podrán manifestar estar de acuerdo o no con la prórroga de los tres (3) meses adicionales.",
        },
        {
          id: "servicios_justicia",
          icon: justiceServicesIcon,
          title: "Servicios de justicia SFC",
          description: "Los jueces de la Superintendencia Financiera de Colombia (SFC) conocen de las controversias surgidas entre los consumidores financieros y las entidades vigiladas, a través del ejercicio de la Acción de Protección al Consumidor Financiero.",
          details: "Se informa que el trámite judicial podrá tener una duración aproximada de un (1) año contado desde la admisión de la demanda, término que podrá prorrogarse hasta por seis (6) meses adicionales.",
          infoItems: [
            {
              text: "Consulte más información de los Servicios de Justicia de la SFC dando",
              linkText: "clic aquí",
            },
          ],
        },
      ].map((option) => ({ ...option, href: startHrefFor(option.id) })),
    };
  },
  mounted() {
    this.focusLegend();
  },
  methods: {
    inputId(option) {
      return `ev-procedure-${option.id}`;
    },
    titleId(option) {
      return `ev-procedure-${option.id}-title`;
    },
    descId(option) {
      return `ev-procedure-${option.id}-desc`;
    },
    isSelected(option) {
      return this.selected === option.id;
    },
    focusLegend() {
      if (this.$refs.legend) this.$refs.legend.focus();
    },
    onBack() {
      // Vuelve al paso anterior del flujo "Crear trámite".
      this.$router.back();
    },
    // onStart(event, option) {
    //   // Destino pendiente: el control se ve activo pero no navega ni agrega PENDING_LINK a la URL.
    //   if (isPendingLink(option.href)) event.preventDefault();
    // },

    onStart(option) {
      // Destino pendiente: el control se ve activo pero no navega ni agrega PENDING_LINK a la URL.
      switch (option.id) {
        case "inconformidad":
          this.$router
            .push("/sfc?procedimiento=radicacion&tipo=queja")
            .catch(() => { });
          break;
        case "conciliacion":
          this.$router
            .push("/sfc?procedimiento=radicacion&tipo=conciliacion")
            .catch(() => { });
          break;
        case "servicios_justicia":
          this.$router
            .push("/sfc?procedimiento=radicacion&tipo=demanda")
            .catch(() => { });

          break;

        default:
          break;
      }
    },
  },
};
</script>

<style scoped>
/*
 * Mismo ancho máximo del contenedor que en question-sfc-procedure.
 */
.single-window-container {
  max-width: 960px;
}

/*
 * El contenido expandido (línea separadora, detalle y botón) se alinea con el
 * texto de la derecha del label: se deja libre a la izquierda el ancho del
 * ícono (w-16 = 4rem) más el gap del label (gap-4 = 1rem en móvil,
 * gap-6 = 1.5rem desde md). Así la línea no inicia debajo del ícono, sino
 * desde donde empieza el texto.
 */
.ventanilla-expanded-content {
  padding-left: 5rem;
  /* 4rem ícono + 1rem gap */
}

@media (min-width: 768px) {
  .ventanilla-expanded-content {
    padding-left: 5.5rem;
    /* 4rem ícono + 1.5rem gap */
  }
}
</style>
